import { Button } from '@/components/ui/button'
import { canOpenPaymentModal, canPaySaleLines } from '@/components/pos/posPayment'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import { PosCustomerCard, PosCustomerSearch } from '@/components/pos/PosCustomerSearch'
import { PosCustomerPackageBalances } from '@/components/pos/PosCustomerPackageBalances'
import { PosLoyaltyPanel } from '@/components/pos/PosLoyaltyPanel'
import { PosTotalsBreakdown } from '@/components/pos/PosTotalsBreakdown'
import { PosFloorPanel } from '@/components/pos/PosFloorPanel'
import { PosOperationsPanel } from '@/components/pos/PosOperationsPanel'

import { POS_PANEL_IDS } from './posViewport'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import { useUiLocale } from '@/contexts/UiLocaleContext'

/**
 * @param {object} props
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {ReturnType<import('./posCart').toPosTotals>} props.totals
 * @param {import('@/types/api').SaleLine | null} [props.selectedLine]
 * @param {import('@/types/api').Stylist[]} props.stylists
 * @param {boolean} props.disabled
 * @param {boolean} [props.hasAddons]
 * @param {string} props.customerError
 * @param {string} props.holdError
 * @param {string} [props.floorQueueError]
 * @param {boolean} [props.canSendToFloor]
 * @param {() => void | Promise<void>} [props.onSendToFloor]
 * @param {(customer: import('@/types/api').Customer) => void} props.onSelectCustomer
 * @param {() => void} props.onClearCustomer
 * @param {() => void} [props.onOpenCustomerHistory]
 * @param {(points: number) => Promise<import('@/types/api').Sale | null>} [props.onRedeemLoyalty]
 * @param {() => Promise<import('@/types/api').Sale | null>} [props.onRemoveLoyaltyRedeem]
 * @param {string} [props.loyaltyError]
 * @param {() => void} props.onOpenPayment
 * @param {string | number} [props.packageBalanceRefreshToken]
 * @param {(actionId: string) => void} props.onOperation
 * @param {() => void | Promise<void>} [props.onHoldTicket]
 */
export function PosCustomerPanel({
  sale,
  totals,
  selectedLine = null,
  stylists,
  disabled,
  hasAddons = false,
  customerError,
  holdError,
  floorQueueError = '',
  canSendToFloor = false,
  onSendToFloor,
  onSelectCustomer,
  onClearCustomer,
  onOpenCustomerHistory,
  onRedeemLoyalty,
  onRemoveLoyaltyRedeem,
  loyaltyError = '',
  onOpenPayment,
  packageBalanceRefreshToken = '',
  onOperation,
  onHoldTicket,
}) {
  const { t } = useUiLocale()
  const customer = getAssignedCustomer(sale)
  const canPay = canPaySaleLines(sale)
  const guestRequired = SALON_DESK_CHECKOUT.requireGuestForCheckout && canPay && !canOpenPaymentModal(sale)

  return (
    <aside
      id={POS_PANEL_IDS.customer}
      className="pos-v2-payment pos-v2-right-panel flex min-h-0 flex-col overflow-hidden bg-white"
      aria-label="Customer, operations, and payment"
    >
      <div className="pos-v2-customer-zone shrink-0 border-b border-[#E5E7EB] p-1.5">
        {customer ? (
          <PosCustomerCard
            customer={customer}
            onClear={onClearCustomer}
            onOpenHistory={onOpenCustomerHistory}
            disabled={disabled}
          />
        ) : (
          <PosCustomerSearch
            onSelectCustomer={onSelectCustomer}
            disabled={disabled}
            error={customerError}
          />
        )}

        {customer && SALON_DESK_CHECKOUT.showFloorQueue ? (
          <div className="mt-1.5">
            <PosFloorPanel sale={sale} disabled={disabled} onHoldTicket={onHoldTicket} />
          </div>
        ) : null}

        {customer && SALON_DESK_CHECKOUT.showPackageBalances ? (
          <div className="mt-1.5">
            <PosCustomerPackageBalances
              sale={sale}
              refreshToken={packageBalanceRefreshToken}
              disabled={disabled}
              compact
              onRedeem={() => onOperation('redeem')}
            />
          </div>
        ) : null}

        {customer && SALON_DESK_CHECKOUT.showLoyaltyRedemption && onRedeemLoyalty && onRemoveLoyaltyRedeem ? (
          <PosLoyaltyPanel
            customerId={customer.id}
            sale={sale}
            disabled={disabled}
            onRedeem={onRedeemLoyalty}
            onRemoveRedeem={onRemoveLoyaltyRedeem}
            error={loyaltyError}
          />
        ) : null}

        {SALON_DESK_CHECKOUT.showTotalsBreakdown ? (
          <PosTotalsBreakdown totals={totals} variant="compact" className="mt-1.5" />
        ) : (
          <div className="pos-v2-balance-strip mt-1.5 flex items-baseline justify-between gap-2">
            <span className="pos-v2-balance-label">Balance due</span>
            <span className="pos-v2-balance-amount font-mono">
              AED {totals.balance_due.toFixed(2)}
            </span>
          </div>
        )}

        {holdError ? (
          <p className="pos-v2-ui-meta mt-1 text-destructive">{holdError}</p>
        ) : null}
        {guestRequired ? (
          <p className="pos-v2-ui-meta mt-1 text-amber-700">{t('pos.guestRequiredHint')}</p>
        ) : null}
        {floorQueueError ? (
          <p className="pos-v2-ui-meta mt-1 text-destructive">{floorQueueError}</p>
        ) : null}
      </div>

      <div className="pos-v2-ops-scroll min-h-0 flex-1 overflow-y-auto">
        <PosOperationsPanel
          selectedLine={selectedLine}
          stylists={stylists}
          disabled={disabled}
          hasAddons={hasAddons}
          onAction={onOperation}
        />
      </div>

      <div className="shrink-0 border-t border-[#E5E7EB] bg-white p-1.5">
        <Button
          className="pos-v2-pay-btn h-12 w-full rounded-md font-bold"
          size="lg"
          disabled={disabled || !canPay}
          onClick={onOpenPayment}
        >
          {SALON_DESK_CHECKOUT.lite ? 'Take payment' : 'Pay'} · AED {totals.balance_due.toFixed(2)}
        </Button>
      </div>
    </aside>
  )
}
